/* Hauser MX — comportamiento de la tarjeta de propiedad (Fase 8).
 *
 * La tarjeta es el elemento más repetido del sitio: aparece en la portada, en
 * el catálogo, en "También podría gustarte" y en los resultados de búsqueda.
 * Antes su interacción era `scale(1.03)` más un cambio de opacidad a la
 * segunda foto. Correcto, y exactamente igual que en cualquier plantilla.
 *
 * ── Escritorio ────────────────────────────────────────────────────────
 * Al entrar el puntero, la foto principal se disuelve en la segunda con el
 * mismo shader de barrido líquido del hero, y la tarjeta se inclina en 3D
 * siguiendo la posición del cursor DENTRO de ella. La inclinación se calcula
 * con la posición real del puntero sobre la tarjeta, no con un valor fijo: el
 * objeto responde al gesto, no lo reproduce.
 *
 * ── El problema de los contextos WebGL, y cómo se resuelve ────────────
 * Un navegador admite ~16 contextos WebGL por pestaña. El catálogo puede
 * mostrar 15 tarjetas y "Propiedades similares" añade 3 más: crear una escena
 * por tarjeta agota el presupuesto y el navegador empieza a matar las más
 * viejas EN SILENCIO — las primeras tarjetas se quedan en negro sin ningún
 * error. Por eso la escena se crea al APUNTAR a la tarjeta y se destruye unos
 * segundos después de salir. En la práctica nunca hay más de dos vivas.
 *
 * ── Móvil ─────────────────────────────────────────────────────────────
 * El hover no existe en táctil, así que ahí el efecto es otro, no es "nada":
 * la tarjeta se inclina y la foto se desplaza dentro de su marco según su
 * posición en la pantalla, con el scroll como motor. Se ve en movimiento, no
 * al tocar. No se usa WebGL en móvil para esto: son transformaciones CSS
 * compuestas por la GPU, que a 15 tarjetas cuestan mucho menos que 15
 * contextos.
 */
(function () {
  'use strict';

  var H = window.Hauser;
  if (!H) return;

  var tarjetas = document.querySelectorAll('[data-hauser-card]');
  if (!tarjetas.length) return;

  var GL = window.HauserGL;
  var finoYCapaz = !H.mqCoarse.matches && GL && GL.disponible();

  /* ── Elegir el escalón de imagen adecuado ─────────────────────────── */

  function mejorFuente(img, anchoCss) {
    var srcset = img.getAttribute('srcset');
    if (!srcset) return img.currentSrc || img.src;
    var objetivo = anchoCss * H.dpr;
    var lista = srcset
      .split(',')
      .map(function (p) {
        var t = p.trim().split(/\s+/);
        return { url: t[0], w: parseInt(t[1], 10) || 0 };
      })
      .filter(function (c) { return c.url && c.w; })
      .sort(function (a, b) { return a.w - b.w; });
    if (!lista.length) return img.currentSrc || img.src;
    for (var i = 0; i < lista.length; i++) {
      if (lista[i].w >= objetivo) return lista[i].url;
    }
    return lista[lista.length - 1].url;
  }

  /* ── Escritorio: shader al apuntar + inclinación ──────────────────── */

  function prepararEscritorio(card) {
    var media = card.querySelector('.hauser-card-propiedad__media');
    if (!media) return;

    var principal = media.querySelector('.hauser-card-propiedad__img--principal');
    var secundaria = media.querySelector('.hauser-card-propiedad__img--hover');
    /* Sin segunda foto no hay transición que hacer. La inclinación sí se
       aplica igual: es la mitad del efecto y no depende de las imágenes. */
    var puedeGL = !!(principal && secundaria);

    var escena = null;
    var temporizadorBaja = null;

    function crear() {
      if (escena || !puedeGL) return;
      var ancho = media.getBoundingClientRect().width;
      escena = new GL.Escena(media, {
        fuentes: [mejorFuente(principal, ancho), mejorFuente(secundaria, ancho)],
        parallax: 0.028,
        intensidad: 0.85,
        grano: 0.015
      });
    }

    function destruir() {
      if (!escena) return;
      escena.destruir();
      escena = null;
    }

    card.addEventListener('pointerenter', function () {
      if (temporizadorBaja) { clearTimeout(temporizadorBaja); temporizadorBaja = null; }
      crear();
      if (escena) {
        escena.hover(true);
        escena.ir(1, 0.9);
      }
      card.classList.add('is-apuntada');
    });

    card.addEventListener('pointerleave', function () {
      card.classList.remove('is-apuntada');
      if (escena) {
        escena.hover(false);
        escena.ir(0, 0.75);
      }
      /* Margen antes de liberar el contexto: recorrer una rejilla con el ratón
         entra y sale de muchas tarjetas, y crear/destruir en cada roce daría
         tirones. 2.5 s cubre el vaivén sin acumular contextos. */
      if (temporizadorBaja) clearTimeout(temporizadorBaja);
      temporizadorBaja = setTimeout(destruir, 2500);

      if (window.gsap) {
        window.gsap.to(card, {
          rotateX: 0, rotateY: 0, duration: 0.7, ease: 'expo.out', overwrite: 'auto'
        });
      }
    });

    /* Inclinación por posición real del puntero dentro de la tarjeta. */
    card.addEventListener('pointermove', function (e) {
      if (!window.gsap) return;
      var r = card.getBoundingClientRect();
      var nx = (e.clientX - r.left) / r.width - 0.5;
      var ny = (e.clientY - r.top) / r.height - 0.5;
      window.gsap.to(card, {
        rotateY: nx * 7,
        rotateX: -ny * 7,
        duration: 0.55,
        ease: 'power2.out',
        overwrite: 'auto'
      });
    });
  }

  /* ── Móvil: inclinación y parallax por posición en pantalla ───────── */

  function prepararMovil(card) {
    var media = card.querySelector('.hauser-card-propiedad__media');
    var img = card.querySelector('.hauser-card-propiedad__img--principal');
    if (!media || !img) return;

    var visible = false;
    H.enVista(card, function (dentro) { visible = dentro; }, { once: false, rootMargin: '10% 0px' });

    var quitar = H.porCuadro(function () {
      if (!visible || !window.gsap) return;
      var r = card.getBoundingClientRect();
      var centro = r.top + r.height / 2;
      /* -1 arriba del todo, 0 en el centro, +1 abajo del todo. */
      var p = (centro / window.innerHeight) * 2 - 1;
      p = Math.max(-1, Math.min(1, p));

      window.gsap.set(card, { rotateX: -p * 3.2 });
      /* La foto se mueve dentro del marco en sentido contrario: el marco es
         una ventana y el contenido está detrás. */
      window.gsap.set(img, { yPercent: p * 3.5, scale: 1.08 });
    });

    /* Si la tarjeta se va del DOM (el catálogo repinta al filtrar), el bucle
       se queda huérfano moviendo un elemento desconectado. */
    if ('IntersectionObserver' in window) {
      var mo = new MutationObserver(function () {
        if (!card.isConnected) { quitar(); mo.disconnect(); }
      });
      if (card.parentNode) mo.observe(card.parentNode, { childList: true });
    }
  }

  /* ── Entrada escalonada, con la inercia del scroll ────────────────── */

  function prepararEntrada() {
    H.listo(function (gsap) {
      var ST = window.ScrollTrigger;
      if (!ST) return;

      H.media(function (ctx) {
        var grupos = document.querySelectorAll('[data-reveal-group]');
        var animaciones = [];

        Array.prototype.forEach.call(grupos, function (grupo) {
          var hijos = grupo.querySelectorAll('[data-hauser-card]');
          if (!hijos.length) return;

          var tw = gsap.fromTo(
            hijos,
            { y: 46, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.9,
              ease: 'expo.out',
              stagger: 0.07,
              scrollTrigger: {
                trigger: grupo,
                scroller: ctx.scroller,
                start: 'top 88%',
                once: true
              }
            }
          );
          animaciones.push(tw);
        });

        return function () {
          animaciones.forEach(function (a) {
            if (a.scrollTrigger) a.scrollTrigger.kill();
            a.kill();
          });
        };
      });
    });
  }

  /* ── Arranque ─────────────────────────────────────────────────────── */

  if (H.tier === 'flat') return; /* El CSS de la fase 4 ya cubre este caso. */

  Array.prototype.forEach.call(tarjetas, function (card) {
    if (finoYCapaz) prepararEscritorio(card);
    else if (H.mqCoarse.matches) prepararMovil(card);
  });

  prepararEntrada();
})();
